'use client'

import Dropdown from '@/components/Dropdown'
import RangeSlider from '@/components/RangeSlider'
import SectionHeading from '@/components/SectionHeading'
import { slugify } from '@/lib/utils'
import { urlForImage } from '@/sanity/lib/image'
import Image from 'next/image'
import { Fragment, useEffect, useState } from 'react'

interface TypefaceSpecimenProps {
	heading: string
	defaultPreviewText?: string
	fonts: any
	initialFontSize?: number
	initialLeading?: number
	bgImage?: any
	bgColour?: string
	textColour?: string
}

export default function TypefaceSpecimen({
	heading,
	defaultPreviewText = 'The Quick Brown Fox Jumps Over The Lazy Dog',
	fonts,
	initialFontSize = 16,
	initialLeading = 1.2,
	bgImage,
	bgColour,
	textColour
}: TypefaceSpecimenProps) {
	if (!fonts || fonts?.length === 0) return null

	const slug = slugify(heading)
	const [selectedFontName, setSelectedFontName] = useState(fonts?.find((font: any) => font?.defaultOption)?.name || fonts[0]?.name)
	const [currentFont, setCurrentFont] = useState(fonts?.find((font: any) => font?.defaultOption) || fonts[0])
	const [previewText, setPreviewText] = useState<string>(defaultPreviewText)
	const [fontSize, setFontSize] = useState<number | null>(null)
	const [leading, setLeading] = useState<number>(initialLeading)
	const [alignment, setAlignment] = useState<"left" | "center" | "right" | "justify">("left")
	const [windowWidth, setWindowWidth] = useState<number | null>(null)

	useEffect(() => {
		setWindowWidth(window.innerWidth)
	}, [])

	useEffect(() => {
		if (windowWidth < 768) {
			setFontSize(Math.floor(initialFontSize / 3))
		} else {
			setFontSize(initialFontSize)
		}
	}, [windowWidth])

	useEffect(() => {
		const font = fonts?.find((font: any) => font?.name === selectedFontName)
		if (font) {
			setCurrentFont(font)
		}
	}, [selectedFontName])

	return (
		<div className={``}>
			<div className="relative z-200 grid items-center grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mb-5">

				<div className="col-span-full sm:col-span-1">
					<SectionHeading heading={heading || 'Typetester'} />
				</div>

				<div className="col-span-full sm:col-span-3 lg:col-span-2 lg:col-start-3 flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-6 md:gap-8">

					<div className="w-full sm:w-1/2 flex-auto">
						{fonts?.length > 0 && (
							<Dropdown
								options={fonts?.map((font: any, index: number) => ({
									label: font?.name,
									value: font?.name,
									defaultValue: 0 === index
								}))}
								onChange={setSelectedFontName}
							/>
						)}
					</div>

					{fontSize && <div className="w-1/3 sm:w-1/4xxx md:max-w-[220px] flex-auto">
						<RangeSlider
							min={8}
							max={240}
							step={1}
							unit={'px'}
							initialValue={fontSize}
							onChange={setFontSize}
						/>
					</div>}

					<div className="w-1/3 sm:w-1/4xxx md:max-w-[220px] flex-auto">
						<RangeSlider
							min={0}
							max={4}
							step={0.1}
							// unit={'px'}
							initialValue={initialLeading}
							onChange={setLeading}
						/>
					</div>

				</div>

			</div>

			<div className={`relative z-100 rounded-[8px]`} style={{
				backgroundColor: bgColour
			}}>
				<div className={`relative w-full aspect-square md:aspect-video rounded-[8px] overflow-clip flex items-center typeface-specimen-${slug} typeface-${slug}-${slugify(currentFont?.name)} overflow-clip`}>
					{bgImage && (
						<Image
							className="absolute z-0 w-full h-full inset-0 object-cover pointer-events-none"
							src={urlForImage(bgImage).width(3840).url()}
							sizes='100vw'
							width={bgImage?.asset?.metadata?.dimensions?.width}
							height={bgImage?.asset?.metadata?.dimensions?.height}
							alt={bgImage?.alt || heading || "Type Tester"}
							placeholder="blur"
							blurDataURL={bgImage?.asset?.metadata?.lqip}
							quality={90}
						/>
					)}
					<span
						className={`relative z-100 w-full appearance-none border-none focus:outline-none resize-none overflow-auto p-5 sm:p-10 md:p-16`}
						contentEditable
						suppressContentEditableWarning
						style={{
							fontSize: `${fontSize}px`,
							lineHeight: `${leading}`,
							textAlign: `${alignment}`,
							backgroundColor: 'transparent',
							color: textColour
						}}
					>{previewText}</span>

					<div className="absolute z-200 top-4 right-4 w-auto flex items-center justify-between gap-2">
						<div className={`flex items-center gap-2 font-500 text-sm leading-100 -tracking-10 text-center text-black dark:text-white bg-white dark:bg-black border border-black dark:border-white px-3 py-2 rounded-[8px]`}>
							<button className={`${alignment === "left" ? "opacity-100" : "opacity-30"} expand-touch relative`} onClick={() => setAlignment("left")}>
								<svg className="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter"><line x1="21" x2="3" y1="6" y2="6"></line><line x1="15" x2="3" y1="12" y2="12"></line><line x1="17" x2="3" y1="18" y2="18"></line></svg>
							</button>
							<button className={`${alignment === "center" ? "opacity-100" : "opacity-30"} expand-touch relative`} onClick={() => setAlignment("center")}>
								<svg className="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter"><line x1="21" x2="3" y1="6" y2="6"></line><line x1="17" x2="7" y1="12" y2="12"></line><line x1="19" x2="5" y1="18" y2="18"></line></svg>
							</button>
							<button className={`${alignment === "right" ? "opacity-100" : "opacity-30"} expand-touch relative`} onClick={() => setAlignment("right")}>
								<svg className="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter"><line x1="21" x2="3" y1="6" y2="6"></line><line x1="21" x2="9" y1="12" y2="12"></line><line x1="21" x2="7" y1="18" y2="18"></line></svg>
							</button>
							<button className={`${alignment === "justify" ? "opacity-100" : "opacity-30"} expand-touch relative`} onClick={() => setAlignment("justify")}>
								<svg className="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter"><line x1="3" x2="21" y1="6" y2="6"></line><line x1="3" x2="21" y1="12" y2="12"></line><line x1="3" x2="21" y1="18" y2="18"></line></svg>
							</button>
						</div>
					</div>
				</div>
				{fonts?.map((font: any, index: number) => {
					const url = font?.file?.asset?.url
					const urlParts = url?.split('/')
					const filename = urlParts[urlParts.length - 1]
					const extension = filename?.split('.').pop()
					return (
						<Fragment key={`${slug}-${slugify(font?.name)}-style-${index}`}>
							<style jsx global>{`
								@font-face {
									font-family: '${slugify(font?.name)}';
									src: url('/api/fonts/${filename}') format('${extension}');
									font-style: ${font?.style};
									font-display: block;
								}
								.typeface-${slug}-${slugify(font?.name)} {
									font-family: '${slugify(font?.name)}' !important;
									font-style: ${font?.style} !important;
								}
							`}</style>
						</Fragment>
					)
				})}
			</div>
		</div>
	)
}